import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { FeatureFlagKey, IFeatureFlagsResponse } from "@miplatform/types";
import { bugsnag } from "@miplatform/utils";

const INITIAL_FEATURE_FLAGS = Object.values(FeatureFlagKey).reduce(
  (acc, value) => {
    acc[value as FeatureFlagKey] = false;

    return acc;
  },
  {} as IFeatureFlagsResponse
);

type FeatureFlagContextProperties = {
  featureFlags: IFeatureFlagsResponse;
  getFeatureFlags: () => Promise<void>;
};

const FeatureFlagContext = createContext<FeatureFlagContextProperties>({
  featureFlags: { ...INITIAL_FEATURE_FLAGS },
  getFeatureFlags: async () => {},
});

export const FeatureFlagProvider: FC<PropsWithChildren> = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState<IFeatureFlagsResponse>({
    ...INITIAL_FEATURE_FLAGS,
  });

  const getFeatureFlags = useCallback(async () => {
    try {
      const response = await subscriptionService.tenantConfig.getFeatureFlags();
      if (response.status === HTTP_STATUSES.OK) {
        setFeatureFlags(response.data);
      }
    } catch (error) {
      bugsnag.notify(error);
    }
  }, []);

  const value = useMemo(
    () => ({ featureFlags, getFeatureFlags }),
    [featureFlags, getFeatureFlags]
  );

  useEffect(() => {
    getFeatureFlags();
  }, [getFeatureFlags]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagsContext = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlagContext must be used within a FeatureFlagProvider"
    );
  }

  return context;
};
